/**
 * What the printed code carries, and how the same code is read back
 *
 * One file for both directions, and that is the whole point: the slip is **written** here
 * (`barcodeText`, `qrText`) and a scan is **read** here (`parseScan`). Written in two places those
 * two drift, and the way that shows is a paper the counter's own scanner does not understand.
 *
 * It had drifted once already, in exactly that shape: the shell wrote `CODE-CONTROL` with a dash
 * while the verification screen split a scan on `*`. Nothing failed, because no QR had ever been
 * printed - the two halves simply never met.
 *
 * THREE FORMS REACH THE READER, AND ALL THREE COME THROUGH ONE EVENT
 *
 *     CODE*CONTROL                  the barcode, read by the scanner at the counter
 *     https://…?id=CODE*CONTROL     the QR, photographed with a phone
 *     CODE                          a code typed in by hand, which has no control code
 *
 * A QR built before this file changed still reads `?ticketCode=…&codeControl=…` - `parseScan` answers
 * both, `qrText` only ever writes the first from here on.
 */
import type { PrintSlip } from '../../models/betslip.ts';

/**
 * The paper, as much of it as these five functions read
 *
 * `Partial<PrintSlip>` and not a shape declared here (rule 2): the slip is described once, in
 * `models/betslip.ts`, and a second description of the same five fields is the drift this whole file
 * exists to prevent. `Partial` because what reaches here is what the caller was given - the shell
 * prints what it has, the print page is handed a ticket, and a copy deliberately has no control
 * code; every function below already answers to a missing field rather than assuming one.
 */
export type SlipCode = Partial<PrintSlip>;

/**
 * A ledger entry, as much of it as `ticketOf` reads
 *
 * Deliberately **not** `models/transaction.LedgerEntry`, and for the same reason `LottoMarkets` does
 * not type a market code as the keys of its own table: answering about a row this build does not
 * recognise **is the job**. `entryType` is compared against a list, and a type that could only ever
 * hold listed values would make the comparison - and the voucher it keeps out - unreachable.
 */
export type LedgerEntryRef = { entryType?: string | null; reference?: string | null };

export const SEPARATOR = '*';

/**
 * What the scanner reads: `CODE*CONTROL`
 *
 * An asterisk, not a dash. The code alphabet is `123456789ABCDEFGHJKMNPQRSTUVWXYZ` - without zero
 * and the letters O, I and L, which are confused when read aloud - so neither of those two
 * characters can appear inside a code and both would work as a separator. `*` won because the
 * verification screen already expected it.
 *
 * **A copy carries the code only.** The control code is what pays a ticket out and cancels it, so a
 * copy that carried it would be a second paper of the same value.
 */
export const barcodeText = (slip: SlipCode): string => ((slip.copy !== true
    && slip.codeControl != null && String(slip.codeControl) !== '')
    ? `${slip.code}${SEPARATOR}${slip.codeControl}`
    : String(slip.code));

/**
 * What the QR carries: the verification address, with the codes in the query
 *
 * The barcode and the QR are not read by the same people. The barcode is for the scanner at the
 * counter; the QR the player photographs with a phone, and there the bare text `ABC*1234` means
 * nothing to them. An address opens a page that shows them their slip.
 *
 * The counter understands it anyway: `parseScan` pulls both codes back out of the address, so a
 * scanned QR opens a slip exactly as a scanned barcode does.
 *
 * Without a configured address (`print.qrBaseUrl` empty) the QR carries the same text as the
 * barcode - the paper then works at the counter and not on a phone, which is better than a QR that
 * works nowhere.
 */
export const qrText = (slip: SlipCode): string =>
{
    const base = String(slip.qrBaseUrl == null ? '' : slip.qrBaseUrl).trim();

    if (base === '')
    {
        return barcodeText(slip);
    }

    try
    {
        const url = new URL(base);

        /**
         * One parameter, `barcodeText`'s own shape - the same `CODE*CONTROL` the scanner reads, not two
         * separate `ticketCode`/`codeControl` params.
         *
         * `parseScan` still reads the older two-param shape too (see its own comment), so a `qrBaseUrl`
         * already saved with the old convention keeps working; this is what a newly-generated address
         * looks like from here on.
         */
        url.searchParams.set('id', barcodeText(slip));

        return url.toString();
    }
    catch
    {
        // a wrong address in the setting must not leave the paper without a code
        return barcodeText(slip);
    }
};

/**
 * Which symbology this machine prints, reduced to one of the two this code can draw
 *
 * A setting of the terminal (`print.barcodeFormat`, migration 116): which scanner stands in which
 * shop is not something the code can know. An unknown value **falls back to the barcode** rather
 * than refusing to print - both codes stand on the paper as text anyway, so a wrong setting means a
 * slower counter, while an unprinted slip means a bet the player cannot prove.
 */
export const formatOf = (slip: SlipCode | null | undefined): 'qr' | 'code128' => ((String((slip != null && slip.barcodeFormat != null) ? slip.barcodeFormat : '')
    .toLowerCase() === 'qr')
    ? 'qr'
    : 'code128');

/**
 * What goes **into** the code, for whichever symbology this machine prints
 *
 * The text is not the same for the two, and that is the difference most easily missed: the barcode
 * carries `CODE*CONTROL` because the scanner at the counter reads it, the QR an address because a
 * phone reads it.
 */
export const codeText = (slip: SlipCode): string => ((formatOf(slip) === 'qr') ? qrText(slip) : barcodeText(slip));

/**
 * A scan, back into the two codes
 *
 * Three forms arrive through one event, so one function reads them: the barcode, the QR address, and
 * a code somebody typed by hand. The address is **not** upper-cased before it is read - a query
 * string is case sensitive, and `HTTPS://` stops being an address.
 *
 * @param text what the scanner or the camera produced
 */
export const parseScan = (text: string | null | undefined): { ticketCode: string; codeControl: string } =>
{
    const raw = String((text == null) ? '' : text).trim();

    if (/^https?:\/\//i.test(raw))
    {
        try
        {
            const { searchParams } = new URL(raw);
            const id = searchParams.get('id');

            /**
             * `id=CODE*CONTROL`, `qrText`'s own current shape - split the same way a bare scan is,
             * a few lines below.
             *
             * The older `?ticketCode=…&codeControl=…` two-param form is still read too, unguarded by a
             * version number or a feature flag: a `qrBaseUrl` saved before this shape existed keeps
             * producing that form until someone re-saves it, and a paper already printed with it does
             * not stop scanning the day the code changes.
             */
            if (id != null)
            {
                const [ticketCode, codeControl] = String(id).toUpperCase().split(SEPARATOR);

                return { ticketCode: ticketCode || '', codeControl: codeControl || '' };
            }

            return {
                ticketCode: String(searchParams.get('ticketCode') || '').trim().toUpperCase(),
                codeControl: String(searchParams.get('codeControl') || '').trim().toUpperCase(),
            };
        }
        catch
        {
            // an address that cannot be read is not a code; it goes on as ordinary text
        }
    }

    const [ticketCode, codeControl] = raw.toUpperCase().split(SEPARATOR);

    return { ticketCode: ticketCode || '', codeControl: codeControl || '' };
};

/**
 * The other direction of the same code: a ledger entry, back to the ticket it is about
 *
 * `transaction_entry.reference` is one column carrying several kinds of thing - a ticket, a voucher, a
 * manual correction - so a reader that wants "which ticket is this" has to know which of them it is
 * looking at. That knowledge is here for the same reason the two directions above are: the ticket
 * module **writes** the reference, the screens **read** it, and written in two places the two drift.
 *
 * **The shape cannot be the answer, and that is the whole trap.** A voucher code is generated from
 * almost the same alphabet and is ten characters long as well (`Voucher.generateCode`), so a check
 * that only measures the text calls a voucher a ticket - and the screen then offers to open a slip
 * that was never issued. `lib/LedgerCleanup.isTicketCode` is exactly such a check, and it is used to
 * **delete** entries.
 *
 * What tells them apart is `entryType`, because the type says what happened: a payin, a payout, the
 * fee or the withheld tax are things that happen to a ticket, and nothing else writes them.
 */
export const PAYOUT_PREFIX = 'payout-';

/**
 * The reference a payout is booked under
 *
 * A payin and a payout are two events on one ticket, so they cannot share a reference - the second
 * one would confirm the first one's reservation. The prefix is written **here** so the writer
 * (`services/ticket/handlers/Wallet`) and the reader below cannot disagree about it.
 *
 * @param code the ticket code
 */
export const payoutReference = (code: string): string => `${PAYOUT_PREFIX}${code}`;

/**
 * Which entry types carry a ticket code.
 *
 * `VOUCHER_*` is deliberately **not** here: those carry a voucher code of the same shape. Nor is
 * `TRANSFER`, `DEPOSIT` or `WITHDRAWAL`, whose reference is whatever the caller wrote.
 */
export const LEDGER_TICKET_TYPES = [
    'PAYIN', 'CASH_PAYIN', 'PAYOUT', 'CASH_PAYOUT', 'WIN_TAX', 'FEE',

    /** the other leg of a payin: what it was allocated to (migration 150) */
    'STAKE',
];

/**
 * The ticket code a ledger entry is about, or `null`
 *
 * @param entry the ledger entry: `{ entryType, reference }`
 * @returns the ticket code, without the payout prefix
 */
export const ticketOf = (entry: LedgerEntryRef | null | undefined): string | null =>
{
    const type = String((entry != null && entry.entryType != null) ? entry.entryType : '')
        .trim().toUpperCase();

    if (!LEDGER_TICKET_TYPES.includes(type))
    {
        return null;
    }

    let code = String((entry != null && entry.reference != null) ? entry.reference : '').trim();

    if (code.toLowerCase().startsWith(PAYOUT_PREFIX))
    {
        code = code.slice(PAYOUT_PREFIX.length);
    }

    /**
     * A last guard against junk, and **not** against vouchers - those pass it, and the type above is
     * what keeps them out. A reference that is not a code at all (a reversal somebody wrote by hand)
     * would otherwise be offered as a slip that cannot be opened.
     */
    return (/^[0-9A-Z]{6,16}$/.test(code.toUpperCase())) ? code.toUpperCase() : null;
};

export default {
    SEPARATOR, barcodeText, qrText, formatOf, codeText, parseScan,
    PAYOUT_PREFIX, payoutReference, LEDGER_TICKET_TYPES, ticketOf,
};
